import { Card, CardHeader } from '@/components/ui';
import type { McpAuthMethod } from '@/types/mcp';

interface Props {
  methods: readonly McpAuthMethod[];
  selectedKind: McpAuthMethod['kind'];
  onChange: (kind: McpAuthMethod['kind']) => void;
}

/**
 * Canonical UI order, regardless of catalog order: OAuth (any variant) →
 * API key → Manual OAuth → anything we don't recognise yet.
 */
const AUTH_METHOD_RANK: Record<McpAuthMethod['kind'], number> = {
  'oauth-dcr': 0,
  'oauth-manual-client': 0,
  'api-key': 1,
  'oauth-paste-token': 2,
};

export function sortAuthMethods<T extends McpAuthMethod>(
  methods: readonly T[],
): T[] {
  return [...methods].sort(
    (a, b) =>
      (AUTH_METHOD_RANK[a.kind] ?? 99) - (AUTH_METHOD_RANK[b.kind] ?? 99),
  );
}

function methodLabel(kind: McpAuthMethod['kind']): string {
  switch (kind) {
    case 'oauth-manual-client':
      return 'OAuth';
    case 'oauth-dcr':
      return 'OAuth (auto-registered)';
    case 'api-key':
      return 'API key';
    case 'oauth-paste-token':
      return 'OAuth Refresh Token';
    default:
      // Defensive — render the raw kind rather than going blank if the
      // backend adds a new variant before the frontend ships its handler.
      return kind;
  }
}

function methodHint(kind: McpAuthMethod['kind']): string {
  switch (kind) {
    case 'oauth-manual-client':
      return 'Per-user OAuth. Each member consents once; the agent uses its owner’s connection at runtime.';
    case 'oauth-dcr':
      return 'Per-user OAuth with automatic client registration. Each member consents on first use.';
    case 'api-key':
      return 'Paste an API key — no admin consent flow. Best when each install can share one credential.';
    case 'oauth-paste-token':
      return 'Paste a refresh token you already obtained (e.g. from a local CLI auth flow).';
    default:
      return '';
  }
}

/**
 * Radio-style picker for the provider's `authMethods`. Hidden by the caller
 * when only one method is available (auto-selected upstream).
 */
export function McpAuthMethodChooser({ methods, selectedKind, onChange }: Props) {
  const ordered = sortAuthMethods(methods);
  return (
    <Card>
      <CardHeader
        title="Connection method"
        description="Choose how this server authenticates. The fields below adjust to match."
      />
      <div className="space-y-2">
        {ordered.map((method) => {
          const id = `auth-method-${method.kind}`;
          const checked = method.kind === selectedKind;
          return (
            <label
              key={method.kind}
              htmlFor={id}
              className={`flex items-start gap-3 rounded-md border px-3 py-2 cursor-pointer transition-colors ${
                checked
                  ? 'border-primary-500 bg-accent/40'
                  : 'border-border hover:bg-accent/30'
              }`}
            >
              <input
                id={id}
                type="radio"
                name="auth-method"
                value={method.kind}
                checked={checked}
                onChange={() => onChange(method.kind)}
                className="mt-1"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground">
                  {methodLabel(method.kind)}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {methodHint(method.kind)}
                </div>
              </div>
            </label>
          );
        })}
      </div>
    </Card>
  );
}
